// src/pages/Designations.jsx
import React, { useEffect, useMemo, useState, useContext, forwardRef, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { SocieteContext } from "../context/SocieteContext";
import { notify } from "../ui/notify";

/* -------------------------------- UI primitives -------------------------------- */
function SkyTab({ active = false, variant = "default", children, className = "", ...rest }) {
  const isPrimary = active || variant === "primary";
  return (
    <button
      {...rest}
      className={[
        "h-9 px-4 rounded-full text-[13px] border transition",
        isPrimary ? "bg-sky-700 text-white border-sky-700 hover:bg-sky-600"
          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
        className,
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function useOutsideClose(refs, onClose) {
  React.useEffect(() => {
    const onDocClick = (e) => {
      const isInside = refs.some((r) => r.current && r.current.contains(e.target));
      if (!isInside) onClose?.();
    };
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [refs, onClose]);
}
function FiltersDropdown({ open, onClose, values, setValues, unitOptions }) {
  const popRef = useRef(null);
  useOutsideClose([popRef], () => open && onClose());
  if (!open) return null;

  return (
    <div ref={popRef} className="absolute z-[1200] mt-2 w-[320px] rounded-xl border bg-white shadow-xl p-3">
      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Unité</div>
          <input
            list="designation-unites"
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="Tape une unité…"
            value={values.unite || ""}
            onChange={(e) => setValues(v => ({ ...v, unite: e.target.value }))}
          />
          <datalist id="designation-unites">
            {unitOptions.map(u => <option key={u} value={u} />)}
          </datalist>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">PU HT (min / max)</div>
          <div className="flex items-center gap-2">
            <input
              type="number" step="0.01"
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="min"
              value={values.puMin ?? ""}
              onChange={(e) => setValues(v => ({ ...v, puMin: e.target.value }))}
            />
            <input
              type="number" step="0.01"
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="max"
              value={values.puMax ?? ""}
              onChange={(e) => setValues(v => ({ ...v, puMax: e.target.value }))}
            />
          </div>
        </div>

        <div className="pt-2 flex items-center justify-end gap-2">
          <button
            className="inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition bg-white border border-gray-300 text-slate-800 hover:bg-gray-50"
            onClick={() => setValues({})}
          >
            Réinitialiser
          </button>
          <button
            className="inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition bg-sky-700 hover:bg-sky-600 text-white"
            onClick={onClose}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
function LinkAction({ children, className = "", ...rest }) {
  return (
    <button
      {...rest}
      className={[
        "px-0 h-auto text-[13px] font-medium",
        "text-sky-700 hover:text-sky-800 hover:underline underline-offset-2",
        "focus:outline-none focus:ring-2 focus:ring-sky-300 rounded",
        className,
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/* mini switch % / € */
function TinyPctEurSwitch({ value = "pct", onChange }) {
  return (
    <span className="inline-flex rounded-full border border-slate-300 overflow-hidden">
      <button type="button" onClick={() => onChange?.("pct")}
        className={"px-2 h-6 text-[11px] " + (value === "pct" ? "bg-slate-900 text-white" : "bg-white text-slate-600")}
        title="Marge en %">%</button>
      <button type="button" onClick={() => onChange?.("eur")}
        className={"px-2 h-6 text-[11px] " + (value === "eur" ? "bg-slate-900 text-white" : "bg-white text-slate-600")}
        title="Marge en €">€</button>
    </span>
  );
}

/* -------------------------------- Quill setup ---------------------------------- */
try {
  const SizeStyle = Quill.import("attributors/style/size");
  SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
  if (typeof window !== "undefined" && !window.__PL_SIZE_REGISTERED__) {
    Quill.register(SizeStyle, true); window.__PL_SIZE_REGISTERED__ = true;
  }
} catch { }

const SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "24px", "32px"];
const quillModules = { toolbar: { container: "#toolbar-designations" } };
const quillFormats = ["size", "bold", "italic", "underline", "align", "list", "bullet", "link", "clean"];

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0)) + " €";
const fmtPct = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n || 0)) + " %";

const isProbablyHtml = (s = "") => /<\/?[a-z][\s\S]*>/i.test(s);
const textToHtml = (txt = "") => (txt.trim()
  ? txt.trim().split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("")
  : "<p></p>");
const ensureHtml = (val = "") => (isProbablyHtml(val) ? val : textToHtml(val));
const toNum = (v) => (typeof v === "number" ? (Number.isFinite(v) ? v : 0)
  : (Number.isFinite(Number(String(v).replace(/\s/g, "").replace(",", ".")))
    ? Number(String(v).replace(/\s/g, "").replace(",", "."))
    : 0));
const r2 = (n) => Number((Number(n || 0)).toFixed(2));

/* ----------------------------- Mapping API <-> UI ------------------------------ */
const toUI = (r) => {
  const meta = r.meta && typeof r.meta === "string" ? (() => { try { return JSON.parse(r.meta); } catch { return {}; } })() : (r.meta || {});
  return {
    id: r.id,
    label: r.label ?? r.titre ?? "",
    prixUnitaire: Number(r.prixUnitaire ?? r.prix ?? 0),
    tva: r.tva !== undefined ? Number(r.tva) : 20,
    cout: Number(meta.cout ?? r.cout ?? 0),
    margePct: meta.margePct !== undefined ? Number(meta.margePct) : null,
    unite: meta.unite ?? r.unite ?? "",
    type: meta.type ?? r.type ?? "labour", // labour | product
  };
};

const toPayload = ({ label, prixUnitaire, tva, cout, margePct, unite, type = "labour" }, extra = {}) => {
  const pu = Number.isFinite(Number(prixUnitaire)) ? Number(prixUnitaire) : 0;
  const tv = Number.isFinite(Number(tva)) ? Number(tva) : 20;
  const c = Number.isFinite(Number(cout)) ? Number(cout) : 0;
  const mp = (margePct === null || margePct === undefined) ? null : (Number.isFinite(Number(margePct)) ? Number(margePct) : null);
  const lbl = String(label || "").trim();
  const meta = { cout: c, margePct: mp, unite: unite || "", type };

  return {
    label: lbl,
    prixUnitaire: pu,
    tva: tv,
    titre: lbl, prix: pu, // compat
    meta: JSON.stringify(meta),
    cout: c, margePct: mp, unite: unite || "", type,
    ...extra,
  };
};

/* Unités proposées */
const UNIT_OPTIONS = ["m²", "m³", "ml", "m", "cm", "mm", "h", "u", "kg", "g", "L", "cl", "mL", "pièce", "lot", "forfait", "jour"];

/* --------------------------- Guard "modifs non enregistrées" ------------------- */
function ModernLeaveModal({ onAction }) {
  return (
    <div className="fixed inset-0 z-[2100]">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]" onClick={() => onAction("cancel")} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-slate-100">
          <button onClick={() => onAction("cancel")}
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
            aria-label="Fermer" title="Fermer">×</button>
          <div className="px-8 pt-8 pb-6 text-center">
            <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-sky-50">
              <svg viewBox="0 0 24 24" width="30" height="30" className="text-sky-600">
                <path fill="currentColor" d="M3 5h12a1 1 0 0 1 1 1v2H2V6a1 1 0 0 1 1-1zm-1 5h14v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V10zm18 2h3v2h-3v-2zm0 4h3v2h-3v-2z" />
              </svg>
            </div>
            <h3 className="text-[18px] font-semibold text-slate-900">Des modifications ne sont pas enregistrées</h3>
            <p className="mt-2 text-[13px] text-slate-600">Si vous quittez maintenant, vos changements seront perdus.</p>
          </div>
          <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-center gap-2">
            <SkyTab onClick={() => onAction("leave")} className="border-rose-300 text-rose-600 hover:bg-rose-50">Quitter sans enregistrer</SkyTab>
            <SkyTab variant="primary" onClick={() => onAction("save")} className="font-medium">Enregistrer et quitter</SkyTab>
          </div>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- Modal Désignation -------------------------------------- */
function DesignationModal({ open, initial, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [margeMode, setMargeMode] = useState("pct");
  const isEdit = Boolean(initial?.id);

  // état & snapshot
  const [form, setForm] = useState({ label: "", cout: 0, prixUnitaire: 0, tva: 20, margePct: null, unite: "", type: "labour" });
  const initialSnapRef = useRef("");

  useEffect(() => {
    const snapObj = {
      label: initial?.label || "", cout: initial?.cout ?? 0, prixUnitaire: initial?.prixUnitaire ?? 0,
      tva: initial?.tva ?? 20, margePct: initial?.margePct ?? null, unite: initial?.unite || "", type: initial?.type || "labour",
    };
    setForm(snapObj);
    initialSnapRef.current = JSON.stringify(snapObj);
  }, [initial?.id, initial?.label, initial?.cout, initial?.prixUnitaire, initial?.tva, initial?.margePct, initial?.unite, initial?.type]);

  const isDirty = useMemo(() => {
    try { return JSON.stringify(form) !== (initialSnapRef.current || ""); } catch { return false; }
  }, [form]);

  // calc
  const cost = toNum(form.cout);
  const pu = toNum(form.prixUnitaire);
  const tva = toNum(form.tva);
  const autoMarge = cost > 0 ? ((pu - cost) / cost) * 100 : 0;
  const margePct = form.margePct === null ? autoMarge : toNum(form.margePct);
  const prixTTC = pu * (1 + tva / 100);

  // handlers
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));
  const onChangePU = (e) => {
    const newPU = toNum(e.target.value);
    const newPct = cost > 0 ? ((newPU - cost) / cost) * 100 : null;
    setForm((f) => ({ ...f, prixUnitaire: e.target.value, margePct: f.margePct === null ? null : r2(newPct) }));
  };
  const onChangeMargePct = (e) => {
    const v = e.target.value;
    if (v === "" || v === null) return setForm((f) => ({ ...f, margePct: null }));
    const m = toNum(v);
    const newPU = cost > 0 ? cost * (1 + m / 100) : pu;
    setForm((f) => ({ ...f, margePct: m, prixUnitaire: r2(newPU) }));
  };
  const onChangeMargeEur = (e) => {
    const benef = toNum(e.target.value);
    const newPU = r2(cost + benef);
    const m = cost > 0 ? ((newPU - cost) / cost) * 100 : null;
    setForm((f) => ({ ...f, prixUnitaire: newPU, margePct: m == null ? null : r2(m) }));
  };

  const submit = async ({ closeAfter = true } = {}) => {
    const rawLabel = String(form.label || "").trim();
    if (!rawLabel) { notify.warning({ title: "Libellé requis", message: "Merci de renseigner la désignation." }); return null; }
    setSaving(true);
    try {
      const saved = await onSaved({
        label: ensureHtml(rawLabel),
        cout: cost, prixUnitaire: pu, tva, unite: form.unite || "", type: form.type || "labour",
        margePct: form.margePct === null ? r2(autoMarge) : margePct,
      });
      if (closeAfter) onClose?.();
      initialSnapRef.current = JSON.stringify(form);
      return saved;
    } finally { setSaving(false); }
  };

  // guard quitter
  const [guardOpen, setGuardOpen] = useState(false);
  const askLeave = () => { if (!isDirty) return onClose?.(); setGuardOpen(true); };
  const handleGuard = async (action) => {
    if (action === "leave") { setGuardOpen(false); return onClose?.(); }
    if (action === "save") { await submit({ closeAfter: true }); setGuardOpen(false); }
    if (action === "cancel") setGuardOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center">
      {/* inputs number sans flèches + quill14 + retrait flèche datalist */}
      <style>{`
        input[type=number]{-moz-appearance:textfield;appearance:textfield;}
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}
        .quill14 .ql-editor{font-size:14px}
        .no-datalist-arrow::-webkit-calendar-picker-indicator{opacity:0; display:none;}
        .no-datalist-arrow::-webkit-list-button{display:none;}
        .no-datalist-arrow::-ms-expand{display:none;}
      `}</style>

      <div className="absolute inset-0 bg-sky-800/40" onClick={askLeave} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[980px] max-w-[95vw] max-h-[90vh] overflow-hidden border">
        {/* Header */}
        <div className="px-5 py-3 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{isEdit ? "Modifier la désignation" : "Nouvelle désignation"}</h3>
            <button onClick={askLeave} className="text-gray-500 hover:text-black">✕</button>
          </div>
          {/* Type (Main d’œuvre / Produit) */}
          <div className="mt-3 flex items-center gap-2">
            <SkyTab
              variant={form.type === "labour" ? "primary" : "default"}
              onClick={() => setForm((f) => ({ ...f, type: "labour" }))}
            >
              Main d’œuvre
            </SkyTab>
            <SkyTab
              variant={form.type === "product" ? "primary" : "default"}
              onClick={() => setForm((f) => ({ ...f, type: "product" }))}
            >
              Produit
            </SkyTab>
          </div>
        </div>

        <div className="p-7 overflow-auto" style={{ maxHeight: "72vh" }}>
          {/* Toolbar + éditeur */}
          <div className="space-y-1">
            <div id="toolbar-designations" className="ql-toolbar ql-snow rounded-t-md">
              <select className="ql-size" defaultValue="12px">
                {SIZE_OPTIONS.map(v => <option key={v} value={v}>{parseInt(v, 10)}</option>)}
              </select>
              <button className="ql-bold" /><button className="ql-italic" /><button className="ql-underline" />
              <select className="ql-align" /><button className="ql-list" value="ordered" />
              <button className="ql-list" value="bullet" /><button className="ql-link" /><button className="ql-clean" />
            </div>
            <ReactQuill
              theme="snow"
              value={ensureHtml(form.label)}
              onChange={(html) => setForm(f => ({ ...f, label: html }))}
              modules={quillModules}
              formats={quillFormats}
              className="quill14 rounded-b-md bg-white"
            />
          </div>

          {/* espace supplémentaire sous le texte */}
          <div className="h-4" />

          {/* Ligne unique bien alignée (12 colonnes) */}
          <div className="grid grid-cols-12 gap-4 items-end">
            {/* Unité */}
            <div className="col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 text-center">Unité</label>
              <input
                type="text"
                list="designation-unites"
                className="no-datalist-arrow h-10 text-[12px] border rounded px-2 text-center w-full"
                placeholder="ex: m², h, u"
                value={form.unite}
                onChange={set('unite')}
              />
              <datalist id="designation-unites">
                {UNIT_OPTIONS.map(u => <option key={u} value={u} />)}
              </datalist>
            </div>

            {/* Coût HT */}
            <div className="col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 text-center">Coût HT €</label>
              <input
                type="number" step="0.01"
                className="h-10 text-[12px] border rounded px-2 text-center w-full"
                placeholder="0.00" value={form.cout}
                onChange={(e) => {
                  const newCost = e.target.value;
                  if (form.margePct !== null) {
                    const m = toNum(form.margePct), c = toNum(newCost);
                    const newPU = c * (1 + (Number.isFinite(m) ? m : 0) / 100);
                    setForm(f => ({ ...f, cout: newCost, prixUnitaire: r2(newPU) }));
                  } else setForm(f => ({ ...f, cout: newCost }));
                }}
              />
            </div>

            {/* PU HT */}
            <div className="col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 text-center">PU HT €</label>
              <input
                type="number" step="0.01"
                className="h-10 text-[12px] border rounded px-2 text-center w-full"
                placeholder="0.00" value={form.prixUnitaire}
                onChange={onChangePU}
              />
            </div>

            {/* TVA */}
            <div className="col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 text-center">TVA %</label>
              <input
                type="number" step="0.1"
                className="h-10 text-[12px] border rounded px-2 text-center w-full"
                placeholder="20" value={form.tva}
                onChange={set('tva')}
              />
            </div>

            {/* Marge */}
            <div className="col-span-2">
              <label className="flex items-center justify-between text-[12px] font-medium text-gray-700 mb-1">
                <span className="mx-auto">Marge</span>
                <span className="ml-2"><TinyPctEurSwitch value={margeMode} onChange={setMargeMode} /></span>
              </label>

              {margeMode === 'pct' ? (
                <input
                  type="number" step="0.1"
                  className="h-10 text-[12px] border rounded px-2 text-center w-full"
                  placeholder={r2(autoMarge)}
                  value={form.margePct === null ? '' : form.margePct}
                  onChange={onChangeMargePct}
                />
              ) : (
                <input
                  type="number" step="0.01"
                  className="h-10 text-[12px] border rounded px-2 text-center w-full"
                  placeholder="0.00"
                  value={r2(pu - cost)}
                  onChange={onChangeMargeEur}
                />
              )}
            </div>

            {/* Prix TTC */}
            <div className="col-span-2">
              <label className="block text-[12px] font-medium text-gray-700 mb-1 text-center">Prix TTC €</label>
              <input
                type="text"
                className="h-10 text-[12px] border rounded px-2 text-center w-full bg-gray-50"
                value={r2(prixTTC)}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between">
          {initial?.id ? <SkyTab onClick={() => setConfirmDeleteOpen(true)}
            className="border-rose-300 text-rose-600 hover:bg-rose-50 font-medium">Supprimer</SkyTab> : <span />}
          <div className="flex gap-2">
            <SkyTab onClick={askLeave}>Annuler</SkyTab>
            <SkyTab variant="primary" onClick={() => submit()} className="font-medium" disabled={saving}>
              {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Ajouter"}
            </SkyTab>
          </div>
        </div>
      </div>

      {guardOpen && <ModernLeaveModal onAction={handleGuard} />}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 z-[2200] grid place-items-center">
          <div className="absolute inset-0 bg-sky-800/40" onClick={() => setConfirmDeleteOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl border">
            <div className="p-5 border-b text-[15px] font-semibold">Supprimer la désignation</div>
            <div className="p-5 text-[13px]">⚠️ Action définitive. Confirmer ?</div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className="px-3 py-2 rounded border" onClick={() => setConfirmDeleteOpen(false)}>Annuler</button>
              <button className="px-3 py-2 rounded bg-rose-600 text-white"
                onClick={async () => { try { await axiosInstance.delete(`/designations/${initial.id}`); notify.success({ title: "Supprimée" }); onClose?.(); onSaved?.(); } catch (e) { notify.error({ title: "Erreur", message: e?.response?.data?.message || "Suppression impossible." }); } }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function ToolbarLeftDesignations({ q, setQ, filters, setFilters, unitOptions }) {
  const [open, setOpen] = useState(false);
  const leftRef = useRef(null);
  const btnRef = useRef(null);
  useOutsideClose([leftRef, btnRef], () => setOpen(false));

  return (
    <div className="relative w-full" ref={leftRef}>
      <div className="inline-flex w-full max-w-full items-stretch h-9">
        <button
          ref={btnRef}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="dropdown-filtres-designations"
          className={
            "px-4 text-sm border border-slate-300 rounded-l-full shrink-0 " +
            (open
              ? "bg-gray-800 text-white"
              : "bg-white hover:bg-gray-50 text-slate-700")
          }
        >
          Filtres
        </button>

        {/* Champ de recherche responsive (flex-1) */}
        <input
          className="px-3 text-sm h-9 rounded-r-full border border-l-0 border-slate-300 bg-gray-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent flex-1 min-w-0"
          placeholder="Rechercher une désignation…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Rechercher une désignation"
          style={{ marginLeft: "-1px" }}
        />
      </div>

      {/* Dropdown des filtres ancré juste en dessous */}
      <div
        id="dropdown-filtres-designations"
        className="absolute left-0 top-full"
      >
        <FiltersDropdown
          open={open}
          onClose={() => setOpen(false)}
          values={filters}
          setValues={setFilters}
          unitOptions={unitOptions}
        />
      </div>
    </div>
  );
}

/* --------------------------------- Page --------------------------------------- */
export default function Designations() {
  const { infosSociete } = useContext(SocieteContext);
  const [marginMode, setMarginMode] = useState("pct"); // affichage tableau
  const [activeType, setActiveType] = useState("all"); // filtre par type (all/labour/product)

  const societeId = infosSociete?.id ?? infosSociete?.societe_id ?? null;
  const projectName = useMemo(() => (infosSociete?.slug || infosSociete?.nom || "").toString().trim(), [infosSociete]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitial, setModalInitial] = useState(null);
  const [filters, setFilters] = useState({ unite: "", puMin: "", puMax: "" });
  const searchRef = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (societeId != null) params.societe_id = societeId;
      if (projectName) params.project = projectName;
      const { data } = await axiosInstance.get("/designations", { params });
      const list = (Array.isArray(data) ? data : [])
        .map(toUI)
        .sort((a, b) => (a.label || "").replace(/<[^>]+>/g, " ").localeCompare((b.label || "").replace(/<[^>]+>/g, " "), "fr", { sensitivity: "base" }));
      setItems(list);
    } catch (e) {
      console.error(e);
      notify.error({ title: "Échec", message: e?.response?.data?.message || "Erreur chargement des désignations" });
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [societeId, projectName]);

  const openCreate = () => { setModalInitial({ label: "", cout: 0, prixUnitaire: 0, tva: 20, margePct: null, unite: "", type: "labour" }); setModalOpen(true); };
  const openEdit = (row) => { setModalInitial({ id: row.id, label: row.label, cout: row.cout ?? 0, prixUnitaire: row.prixUnitaire, tva: row.tva, margePct: row.margePct, unite: row.unite || "", type: row.type || "labour" }); setModalOpen(true); };

  const onModalSaved = async (dataFromModal) => {
    try {
      const payload = toPayload(dataFromModal, { societe_id: societeId ?? undefined, project: projectName || undefined });
      if (modalInitial?.id) {
        const id = modalInitial.id;
        try { await axiosInstance.put(`/designations/${id}`, payload); }
        catch { try { await axiosInstance.patch(`/designations/${id}`, payload); } catch { await axiosInstance.put(`/designation/${id}`, payload); } }
        notify.success({ title: "Mise à jour", message: "Désignation modifiée." });
      } else {
        await axiosInstance.post("/designations", payload);
        notify.success({ title: "Créée", message: "Désignation ajoutée." });
      }
      setModalOpen(false); setModalInitial(null); fetchAll();
    } catch (e) {
      console.error(e);
      notify.error({ title: "Erreur", message: e?.response?.data?.message || "Enregistrement impossible." });
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const puMin = Number(filters.puMin || NaN);
    const puMax = Number(filters.puMax || NaN);
    const byType = (row) => activeType === "all" || (row.type || "labour") === activeType;
    const bySearch = (row) => !needle || (row.label || "").replace(/<[^>]+>/g, " ").toLowerCase().includes(needle);
    const byUnite = (row) => !filters.unite || String(row.unite || "").toLowerCase() === String(filters.unite).toLowerCase();
    const byPu = (row) => {
      const pu = toNum(row.prixUnitaire);
      if (Number.isFinite(puMin) && pu < puMin) return false;
      if (Number.isFinite(puMax) && pu > puMax) return false;
      return true;
    };

    return items
      .filter(byType)
      .filter(bySearch)
      .filter(byUnite)
      .filter(byPu)
      .slice()
      .sort((a, b) =>
        (a.label || "").replace(/<[^>]+>/g, " ").localeCompare((b.label || "").replace(/<[^>]+>/g, " "), "fr", { sensitivity: "base" })
      );
  }, [items, q, activeType, filters]);


  return (
    <div className="p-1">
      {/* Rendu quill 14 dans le tableau (volontairement 12px pour compacité) */}
      <style>{`.quill14 .ql-editor{font-size:12px}`}</style>

      <div className="mb-6"><h1 className="text-xl font-bold text-slate-700">Désignations</h1></div>

      <div className="mb-4 rounded-lg border bg-white px-3 py-2">
        {/* Grille 3 zones : gauche (filtres+recherche) / centre (tabs) / droite (ajouter) */}
        <div className="grid gap-3 items-center md:grid-cols-3">
          {/* GAUCHE : Filtres collé à Recherche */}
          <ToolbarLeftDesignations
            q={q}
            setQ={setQ}
            filters={filters}
            setFilters={setFilters}
            unitOptions={UNIT_OPTIONS}
          />

          {/* CENTRE : Onglets collés */}
          <div className="justify-self-center">
            <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden">
              <button
                type="button"
                className={`px-4 h-9 text-sm ${activeType === "all" ? "bg-gray-300 text-black" : "bg-white text-slate-700"}`}
                onClick={() => setActiveType("all")}
                aria-pressed={activeType === "all"}
              >
                Tous
              </button>
              <button
                type="button"
                className={`px-4 h-9 text-sm border-l border-slate-300 ${activeType === "labour" ? "bg-gray-300 text-black" : "bg-white text-slate-700"}`}
                onClick={() => setActiveType("labour")}
                aria-pressed={activeType === "labour"}
              >
                Main d’œuvre
              </button>
              <button
                type="button"
                className={`px-4 h-9 text-sm border-l border-slate-300 ${activeType === "product" ? "bg-gray-300 text-black" : "bg-white text-slate-700"}`}
                onClick={() => setActiveType("product")}
                aria-pressed={activeType === "product"}
              >
                Produit
              </button>
            </div>
          </div>

          {/* DROITE : Ajouter */}
          <div className="justify-self-end">
            <button
              className="inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition focus:outline-none focus:ring-2 focus:ring-sky-300 bg-sky-700 hover:bg-sky-600 text-white"
              onClick={openCreate}
            >
              Ajouter une nouvelle désignation
            </button>
          </div>
        </div>
      </div>


      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <colgroup>
            {[{}, { style: { width: 80 } }, { style: { width: 110 } }, { style: { width: 130 } }, { style: { width: 110 } },
            { style: { width: 80 } }, { style: { width: 120 } }, { style: { width: 140 } }].map((p, i) => <col key={i} {...p} />)}
          </colgroup>
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="text-left  px-3 py-2">Désignation</th>
              <th className="text-center px-2 py-2">Unité</th>
              <th className="text-right  px-2 py-2">Coût&nbsp;HT</th>
              <th className="text-right  px-2 py-2">
                <div className="flex items-center justify-end gap-2">
                  <span>Marge</span>
                  <TinyPctEurSwitch value={marginMode} onChange={setMarginMode} />
                </div>
              </th>
              <th className="text-right  px-2 py-2">PU&nbsp;HT</th>
              <th className="text-right  px-2 py-2">TVA&nbsp;%</th>
              <th className="text-right  px-2 py-2">Prix&nbsp;TTC</th>
              <th className="text-right  px-2 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const html = isProbablyHtml(row.label);
              const cost = toNum(row.cout);
              const pu = toNum(row.prixUnitaire);
              const tva = toNum(row.tva);
              const pct = row.margePct != null ? row.margePct : cost > 0 ? ((pu - cost) / cost) * 100 : 0;
              const ttc = pu * (1 + tva / 100);
              const benef = pu - cost;

              return (
                <tr key={row.id} className="border-t align-top hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="flex items-start gap-2">
                      <span className={[
                        "inline-flex h-5 px-2 rounded-full text-[11px] items-center justify-center flex-none",
                        (row.type || "labour") === "labour" ? "bg-sky-50 text-sky-700" :
                          (row.type || "labour") === "product" ? "bg-amber-50 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                      ].join(" ")}>
                        {(row.type || "labour") === "labour" ? "MO" : (row.type || "labour") === "product" ? "PR" : "—"}
                      </span>
                      <div className="quill14">
                        {html ? <div className="ql-editor !p-0" dangerouslySetInnerHTML={{ __html: row.label }} />
                          : <div className="ql-editor !p-0">{row.label}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">{row.unite || "—"}</td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{fmtEur(cost)}</td>
                  <td className={"px-2 py-2 text-right tabular-nums whitespace-nowrap " + (benef < 0 ? "text-rose-600" : "")}>
                    {marginMode === "pct" ? fmtPct(pct) : fmtEur(benef)}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{fmtEur(pu)}</td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{fmtPct(tva)}</td>
                  <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">{fmtEur(ttc)}</td>
                  <td className="px-2 pt-2 pb-0 text-right align-top">
                    <LinkAction onClick={() => openEdit(row)}>Modifier</LinkAction>
                  </td>


                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Aucune désignation.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <DesignationModal
        open={modalOpen}
        initial={modalInitial}
        onClose={() => { setModalOpen(false); setModalInitial(null); }}
        onSaved={onModalSaved}
      />
    </div>
  );
}
